/**
 * [DUBAI 2026-10-19, user] A shuffled DECK, the PM Singapore notifier's rule: every item is dealt once, in a random order,
 * before any comes round again; a new round never opens on the item that closed the last one; the same announcement
 * always gets the same item (a re-show of the same deal does not use up the next one). Kept in localStorage so the
 * board's own deploy-reload carries on with the round it was dealing; if storage is blocked the module copy keeps the
 * rule for as long as the page stays open.
 *
 * The Dubai board deals TWO decks this way (user 10-19: "just like the gif will play randomly, i want to add the sounds
 * also work in a random order"): the 18 notifier GIFs (the same files as PM Singapore, a deck of its own) and the five
 * Dubai sounds.
 */
import { NOTIFIER_GIFS, gifUrl } from '../pmSingapore/notifierGifs';

interface Stored<T> { queue: T[]; last: T | null; byId: [string, T][] }

export function makeDeck<T extends string | number>(items: readonly T[], storeKey: string) {
  let mem: Stored<T> | null = null;
  const known = new Set<T>(items);
  const read = (): Stored<T> => {
    try {
      const raw = JSON.parse(window.localStorage.getItem(storeKey) || 'null');
      const queue: T[] = Array.isArray(raw?.queue) ? raw.queue.filter((x: T, i: number, all: T[]) => known.has(x) && all.indexOf(x) === i) : [];
      const byId: [string, T][] = Array.isArray(raw?.byId) ? raw.byId.filter((p: unknown) => Array.isArray(p) && known.has((p as [string, T])[1])) : [];
      return { queue, last: known.has(raw?.last) ? raw.last : null, byId };
    } catch {
      return { queue: [], last: null, byId: [] };
    }
  };
  const write = (d: Stored<T>): void => {
    try { window.localStorage.setItem(storeKey, JSON.stringify(d)); } catch { /* the module copy keeps the deck */ }
  };
  const current = (): Stored<T> => { if (!mem) mem = read(); return mem; };
  const shuffled = (avoidFirst: T | null): T[] => {
    const list = [...items];
    for (let i = list.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }
    if (list.length > 1 && list[0] === avoidFirst) {
      const k = 1 + Math.floor(Math.random() * (list.length - 1));
      [list[0], list[k]] = [list[k], list[0]];
    }
    return list;
  };
  const topUp = (d: Stored<T>): void => { if (!d.queue.length) d.queue = shuffled(d.last); };
  return {
    /** The item for one announcement: the same id always gets the same item; a new id deals the next one. */
    forId(id: string): T {
      const d = current();
      const had = d.byId.find(([k]) => k === id);
      if (had) return had[1];
      topUp(d);
      const item = d.queue.shift() as T;
      d.last = item;
      d.byId = [...d.byId.filter(([k]) => k !== id), [id, item] as [string, T]].slice(-30);
      topUp(d);
      write(d);
      return item;
    },
    /** What the NEXT new announcement will get (to warm it up early). */
    peekNext(): T {
      const d = current();
      topUp(d);
      return d.queue[0];
    },
  };
}

const gifDeck = makeDeck(NOTIFIER_GIFS, 'dubai-gif-deck');
export const gifFor = (id: string): string => gifUrl(gifDeck.forId(id));

/* Held so the request is not dropped with the element before it completes. */
let warming: HTMLImageElement | null = null;
/** Fetch the GIF the next new deal will get, so its card never opens on a GIF still downloading (the biggest is 6.5 MB). */
export function warmNextGif(): void {
  const url = gifUrl(gifDeck.peekNext());
  if (warming && warming.getAttribute('src') === url) return;
  warming = new Image();
  warming.decoding = 'async';
  warming.src = url;
}
